/**
 * Feuille « POI manuel » — EF-POI-004, EF-NAV-006.
 *
 * Trois modes de saisie de position ; le mode par progression est proposé par
 * défaut, l'application ne comportant pas d'affichage cartographique.
 */

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { CATEGORIES, type CategoryKey } from '../../core/categories';
import { LIMITS } from '../../core/parameters';
import { ACTIONS, SHEETS } from '../labels';
import { NumberField } from './NumberField';
import { Sheet } from './Sheet';
import { closeSheet, document_, editingPoiId, raceState, saveManualPoi, type ManualPoiDraft } from '../state';

export function ManualPoiSheet(): JSX.Element {
  const current = document_.value;
  const editingId = editingPoiId.value;
  const existing = current === null || editingId === null ? null : (current.pois.get(editingId) ?? null);

  const [draft, setDraft] = useState<ManualPoiDraft>(() => ({
    id: existing?.id ?? null,
    category: (existing?.categories[0] ?? 'checkpoint') as CategoryKey,
    mode: existing?.manualInput ?? 'progression',
    lat: existing?.coordinate.lat ?? 45,
    lon: existing?.coordinate.lon ?? 3,
    progressionKm: (existing?.passages[0]?.progressionMeters ?? 0) / 1000,
    name: existing?.name ?? '',
    openingHours: existing?.openingHours ?? '',
    phone: existing?.phone ?? '',
    email: existing?.email ?? '',
    website: existing?.website ?? '',
    note: existing?.userState.note ?? '',
  }));

  const close = (): void => {
    editingPoiId.value = null;
    closeSheet();
  };

  if (current === null) {
    return (
      <Sheet title={SHEETS.manualPoi} onClose={close}>
        <p>Chargez un parcours avant d’ajouter un POI manuel.</p>
      </Sheet>
    );
  }

  const lengthKm = current.route.track.lengthMeters / 1000;
  const patch = (values: Partial<ManualPoiDraft>): void => {
    setDraft((previous) => ({ ...previous, ...values }));
  };

  const nameLength = [...draft.name.normalize('NFC')].length;
  const noteLength = [...draft.note.normalize('NFC')].length;
  const raceActive = raceState.value.active;

  return (
    <Sheet title={SHEETS.manualPoi} onClose={close}>
      <label>
        <span class="field-label">Catégorie (obligatoire)</span>
        <select
          value={draft.category}
          onChange={(event) => {
            patch({ category: (event.currentTarget as HTMLSelectElement).value as CategoryKey });
          }}
        >
          {CATEGORIES.map((definition) => (
            <option key={definition.key} value={definition.key}>
              {definition.label}
            </option>
          ))}
        </select>
      </label>
      <p class="muted small">
        Un POI manuel porte exactement une catégorie. L’activation d’une catégorie dans les paramètres de
        recherche ne restreint pas la création manuelle.
      </p>

      <label>
        <span class="field-label">Mode de saisie de la position</span>
        <select
          value={draft.mode}
          onChange={(event) => {
            patch({ mode: (event.currentTarget as HTMLSelectElement).value as ManualPoiDraft['mode'] });
          }}
        >
          <option value="progression">Par progression depuis le départ</option>
          <option value="coordinates">Par coordonnées</option>
          <option value="currentPosition" disabled={!raceActive}>
            Par position courante {raceActive ? '' : '(mode course uniquement)'}
          </option>
        </select>
      </label>

      {draft.mode === 'progression' ? (
        <NumberField
          label="Progression depuis le départ"
          suffix="km"
          value={draft.progressionKm}
          min={0}
          max={lengthKm}
          step={0.1}
          onChange={(value) => {
            patch({ progressionKm: value });
          }}
        />
      ) : (
        <>
          <NumberField
            label="Latitude"
            value={draft.lat}
            min={-90}
            max={90}
            step={0.0000001}
            onChange={(value) => {
              patch({ lat: value });
            }}
          />
          <NumberField
            label="Longitude"
            value={draft.lon}
            min={-180}
            max={180}
            step={0.0000001}
            onChange={(value) => {
              patch({ lon: value });
            }}
          />
        </>
      )}

      <label>
        <span class="field-label">
          Nom ({nameLength} / {LIMITS.manualNameCodePoints})
        </span>
        <input
          type="text"
          value={draft.name}
          onInput={(event) => {
            const value = (event.currentTarget as HTMLInputElement).value;
            if ([...value.normalize('NFC')].length <= LIMITS.manualNameCodePoints) patch({ name: value });
          }}
        />
      </label>
      <label>
        <span class="field-label">Horaires d’ouverture</span>
        <input
          type="text"
          value={draft.openingHours}
          onInput={(event) => {
            patch({ openingHours: (event.currentTarget as HTMLInputElement).value });
          }}
        />
      </label>
      <label>
        <span class="field-label">Téléphone</span>
        <input
          type="tel"
          value={draft.phone}
          onInput={(event) => {
            patch({ phone: (event.currentTarget as HTMLInputElement).value });
          }}
        />
      </label>
      <label>
        <span class="field-label">Adresse électronique</span>
        <input
          type="email"
          value={draft.email}
          onInput={(event) => {
            patch({ email: (event.currentTarget as HTMLInputElement).value });
          }}
        />
      </label>
      <label>
        <span class="field-label">Site web</span>
        <input
          type="url"
          value={draft.website}
          onInput={(event) => {
            patch({ website: (event.currentTarget as HTMLInputElement).value });
          }}
        />
      </label>
      <label>
        <span class="field-label">
          Commentaire ({noteLength} / {LIMITS.noteCodePoints})
        </span>
        <textarea
          value={draft.note}
          placeholder="Ce que vous voulez retrouver ici à 3 h du matin."
          onInput={(event) => {
            const value = (event.currentTarget as HTMLTextAreaElement).value;
            if ([...value.normalize('NFC')].length <= LIMITS.noteCodePoints) patch({ note: value });
          }}
        />
      </label>

      <div class="actions">
        <button type="button" onClick={close}>
          {ACTIONS.cancel}
        </button>
        <button
          type="button"
          class="primary"
          onClick={() => {
            saveManualPoi(draft);
          }}
        >
          {ACTIONS.save}
        </button>
      </div>
    </Sheet>
  );
}
